import { LitElement, html, css, nothing } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

const SVG_ICONS = {
  chevronRight: `<i class='bx bx-chevron-right'></i>`,
  chevronDown: `<i class='bx bx-chevron-down'></i>`,
  move: `<i class='bx bx-move'></i>`,
  chevronsDown: `<i class='bx bx-chevrons-down'></i>`,
  chevronsUp: `<i class='bx bx-chevrons-up'></i>`,
  crosshair: `<i class='bx bx-crosshair'></i>`,
  eye: `<i class='bx bx-eye'></i> `,
  eyeOff: `<i class='bx bx-low-vision'></i> `,
  lock: `<i class='bx bx-lock'></i> `,
  unlock: `<i class='bx bx-lock-open-alt'></i> `,
  trash: `<i class='bx bx-trash'></i>`,
  default: `<i class='bx bx-button'></i> `,
  section: `<i class='bx bx-layout'></i>`,
  text: `<i class='bx bx-superscript'></i> `,
  anchor: `<i class='bx bx-anchor'></i> `,
  container: `<i class='bx bx-cube'></i>`,
  image: `<i class='bx bx-image'></i>`,
  button: `<i class='bx bx-rectangle'></i>`,
  close: `<i class='bx bx-x'></i>`,
};

// Singleton state for drag source (cross-shadow-dom)
let dragSourceNode = null;

export class DomNavigator extends LitElement {
  static properties = {
    root: { type: Object },
    selection: { type: Object },
    autoReveal: { type: Boolean },
    config: { type: Object },
    icons: { type: Object },
    _expanded: { state: true },
    _hovered: { state: true },
    _dragState: { state: true },
    _dropTarget: { state: true },
    _dropPos: { state: true },
    _forbidden: { state: true },
    nodeActions: { type: Array },
    headerActions: { type: Array },
    shouldShow: { attribute: false },
    transformNode: { attribute: false },

    // Callback function properties
    onReady: { type: Function, attribute: false },
    onClick: { type: Function, attribute: false },
    onDblclick: { type: Function, attribute: false },
    onContextmenu: { type: Function, attribute: false },
    onSelect: { type: Function, attribute: false },
    onSelectionChange: { type: Function, attribute: false },
    onExpand: { type: Function, attribute: false },
    onCollapse: { type: Function, attribute: false },
    onDragStart: { type: Function, attribute: false },
    onDragOver: { type: Function, attribute: false },
    onDrop: { type: Function, attribute: false },
    onDragEnd: { type: Function, attribute: false },
    onMove: { type: Function, attribute: false },
    onVisibilityChange: { type: Function, attribute: false },
    onLockChange: { type: Function, attribute: false },
    onDelete: { type: Function, attribute: false },
    onActionClick: { type: Function, attribute: false },
    onHeaderActionClick: { type: Function, attribute: false },
    onRootChange: { type: Function, attribute: false },
    onHoverStart: { type: Function, attribute: false },
    onHoverEnd: { type: Function, attribute: false },
    onKeydown: { type: Function, attribute: false },
  };

  static styles = css`
    rd-navigator {
      display: flex;
      flex-direction: column;
      position: fixed;
      z-index: 9999;
      top: 100px;
      right: 340px;
      width: 280px;
      height: 500px;
      min-width: 200px;
      min-height: 200px;
      background-color: var(--bg-panel);
      border: 1px solid var(--border-color);
      border-radius: 8px;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2);
      font-family: "Inter", sans-serif;
      font-size: 12px;
      color: var(--text-main);
      user-select: none;
    }
    rd-navigator .rdn-window-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 36px;
      padding: 0 8px;
      background: var(--bg-header);
      border-bottom: 1px solid var(--border-color);
      border-radius: 8px 8px 0 0;
      cursor: move;
    }
    rd-navigator .rdn-header-actions {
      display: flex;
      gap: 4px;
    }
    rd-navigator .rdn-h-btn {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--text-muted);
      border-radius: 4px;
      cursor: pointer;
      transition: 0.1s;
    }
    rd-navigator .rdn-h-btn:hover {
      background: var(--item-hover);
      color: var(--text-main);
    }
    rd-navigator .rdn-tree-container {
      flex: 1;
      overflow-y: auto;
      padding: 4px 0;
      position: relative;
    }
    rd-navigator .rdn-tree-container::-webkit-scrollbar {
      width: 6px;
    }
    rd-navigator .rdn-tree-container::-webkit-scrollbar-thumb {
      background: var(--border-color);
      border-radius: 3px;
    }

    rd-navigator .rdn-node {
      display: flex;
      align-items: center;
      height: 28px;
      padding-right: 8px;
      cursor: pointer;
      border: 1px solid transparent;
      transition: background 0.1s;
      position: relative;
    }
    rd-navigator .rdn-node:hover {
      background: var(--item-hover);
    }
    rd-navigator .rdn-node.selected {
      background: var(--selection-bg);
      color: var(--primary-color);
    }
    rd-navigator .rdn-node.hovered {
      background: var(--item-hover);
    }
    rd-navigator .rdn-node.drag-over-inside {
      background: var(--selection-bg);
      box-shadow: inset 0 0 0 2px var(--primary-color);
      border-radius: 4px;
    }

    rd-navigator .rdn-toggle {
      width: 20px;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.5;
    }
    rd-navigator .rdn-toggle:hover {
      opacity: 1;
    }
    rd-navigator .rdn-toggle.invisible {
      visibility: hidden;
    }

    rd-navigator .rdn-icon {
      width: 16px;
      display: flex;
      justify-content: center;
      margin-right: 6px;
      opacity: 0.7;
    }
    rd-navigator .rdn-label {
      white-space: nowrap;
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-right: 6px;
    }
    rd-navigator .rdn-meta {
      color: var(--text-muted);
      font-size: 10px;
      opacity: 0.7;
      font-family: monospace;
    }

    rd-navigator .rdn-node-actions {
      margin-left: auto;
      display: flex;
      gap: 2px;
      opacity: 0;
      transition: opacity 0.1s;
    }
    rd-navigator .rdn-node:hover .rdn-node-actions,
    rd-navigator .rdn-node-actions.visible {
      opacity: 1;
    }
    rd-navigator .rdn-action-btn {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 3px;
      color: var(--text-muted);
    }
    rd-navigator .rdn-action-btn:hover {
      background: rgba(0, 0, 0, 0.05);
      color: var(--text-main);
    }
    rd-navigator .rdn-action-btn.active {
      color: var(--primary-color);
    }
    rd-navigator .rdn-action-btn.delete:hover {
      color: var(--danger-color);
    }

    rd-navigator #rdn-drop-line {
      position: absolute;
      height: 2px;
      background: var(--primary-color);
      pointer-events: none;
      z-index: 100;
      left: 0;
      right: 0;
      display: none;
      transform: translateY(-50%);
    }
    rd-navigator #rdn-drop-line::before {
      content: "";
      position: absolute;
      left: 0;
      top: -2px;
      width: 6px;
      height: 6px;
      background: var(--primary-color);
      border-radius: 50%;
    }

    rd-navigator #rdn-tooltip {
      position: fixed;
      top: 0;
      left: 0;
      z-index: 10000;
      background: #dc2626;
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      pointer-events: none;
      display: none;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
      transform: translate(10px, 10px);
    }
    rd-navigator #rdn-tooltip.show {
      display: block;
    }

    rd-navigator .rdn-resizer {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 15px;
      height: 15px;
      cursor: nwse-resize;
      background: linear-gradient(135deg, transparent 50%, var(--text-muted) 50%);
      opacity: 0.3;
      border-bottom-right-radius: 6px;
    }
    rd-navigator .rdn-resizer:hover {
      opacity: 0.8;
    }
  `;

  constructor() {
    super();
    this._expanded = new Set();
    this._dragState = {
      isDragging: false,
      startX: 0,
      startY: 0,
      top: 100,
      left: window.innerWidth - 700,
    };
    this.autoReveal = false;
    this.icons = {}; // { ...SVG_ICONS };

    this.headerActions = [
      {
        id: "expand",
        icon: "chevronDown",
        title: "Expand All",
        onClick: () => this.expandAll(),
      },
      {
        id: "collapse",
        icon: "chevronRight",
        title: "Collapse All",
        onClick: () => this.collapseAll(),
      },
      {
        id: "reveal",
        icon: "crosshair",
        title: "Reveal Selection",
        onClick: () => this.reveal(this.selection),
      },
      //   {
      //     id: "close",
      //     icon: "close",
      //     title: "Close Navigator",
      //     onClick: () => NavigatorManager.hide(),
      //   },
    ];

    this.nodeActions = [
      {
        id: "visibility",
        title: "Toggle Visibility",
        getIcon: (node) => {
          const hidden = node.hidden || node.getAttribute("data-hidden") === "true";
          return hidden ? "eyeOff" : "eye";
        },
        isActive: (node) => node.hidden || node.getAttribute("data-hidden") === "true",
        onClick: (e, node) => this._toggleVisibility(e, node),
      },
      {
        id: "lock",
        title: "Lock",
        getIcon: (node) => {
          const locked = node.getAttribute("data-locked") === "true";
          return locked ? "lock" : "unlock";
        },
        isActive: (node) => node.getAttribute("data-locked") === "true",
        onClick: (e, node) => this._toggleLock(e, node),
      },
      {
        id: "delete",
        title: "Delete",
        getIcon: () => "trash",
        className: "delete",
        onClick: (e, node) => this._deleteNode(e, node),
      },
    ];

    this.actions = ["visibility", "lock", "delete"];
    this._handleGlobalClick = this._handleGlobalClick.bind(this);
  }

  // Render into light DOM to avoid shadow styling conflicts
  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("click", this._handleGlobalClick);
    window.addEventListener("keydown", this._handleKeydown.bind(this));
    this.addEventListener("dragover", (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
    });
    // Dispatch ready event after component is connected
    requestAnimationFrame(() => this._dispatch("ready", { component: this }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("click", this._handleGlobalClick);
    window.removeEventListener("keydown", this._handleKeydown);
    if (this._observer) this._observer.disconnect();
    if (this._parentObserver) this._parentObserver.disconnect();
  }

  updated(changedProps) {
    if (changedProps.has("root")) {
      this._initObserver();
      this._initCanvasDrag();
      this._dispatch("root-change", { root: this.root, oldRoot: changedProps.get("root") });
    }
    if (changedProps.has("selection") && this.selection && this.autoReveal) {
      this.reveal(this.selection);
    }
  }

  // --- API METHODS ---

  select(node, options = { reveal: true, noEvent: false }) {
    const oldSelection = this.selection;
    if (oldSelection === node) return;

    // Prevent re-entry during selection to avoid infinite loops
    if (this._isSelecting) return;
    this._isSelecting = true;

    try {
      // Update selection BEFORE dispatching events so guards work in callbacks
      this.selection = node;

      if (!options.noEvent) {
        if (!this._dispatch("select", { node, oldSelection })) {
          // If event was cancelled, restore old selection
          this.selection = oldSelection;
          return;
        }

        this._dispatch("selection-change", { node, oldSelection });
      }
      if (options.reveal !== false) this.reveal(node);
    } finally {
      this._isSelecting = false;
    }
  }

  expand(node) {
    if (!this._dispatch("expand", { node })) return;
    this._expanded.add(node);
    this.requestUpdate();
  }

  collapse(node) {
    if (!this._dispatch("collapse", { node })) return;
    this._expanded.delete(node);
    this.requestUpdate();
  }

  toggle(node) {
    if (this._expanded.has(node)) this.collapse(node);
    else this.expand(node);
  }

  expandAll(context = this.root) {
    if (!context) return;
    this._expanded.add(context);
    const walker = document.createTreeWalker(context, NodeFilter.SHOW_ELEMENT);
    while (walker.nextNode()) this._expanded.add(walker.currentNode);
    this.requestUpdate();
  }

  collapseAll() {
    this._expanded.clear();
    this.requestUpdate();
  }

  async reveal(node) {
    if (!node || !this.root.contains(node)) return;

    let p = node.parentNode;
    while (p && p !== document.body) {
      if (this.root.contains(p) || p === this.root) this._expanded.add(p);
      p = p.parentNode;
    }
    this.requestUpdate();
    await this.updateComplete;
    const rows = this.querySelectorAll(".rdn-node");
    for (const row of rows) {
      if (row.node === node) {
        row.scrollIntoView({ block: "center", behavior: "smooth" });
        break;
      }
    }
  }

  // --- INTERNAL ---

  _initObserver() {
    if (this._observer) this._observer.disconnect();
    if (this._parentObserver) this._parentObserver.disconnect();

    if (!this.root) return;

    this._observer = new MutationObserver(() => this.requestUpdate());
    this._observer.observe(this.root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["id", "class", "data-name", "data-tagname", "hidden", "data-hidden", "data-locked"],
    });

    if (this.root.parentNode) {
      this._parentObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const r of m.removedNodes) {
            if (r === this.root) {
              this.requestUpdate();
            }
          }
        }
      });
      this._parentObserver.observe(this.root.parentNode, { childList: true });
    }
  }

  _dispatch(name, detail) {
    // Call direct callback properties with plain data object (not CustomEvent)
    const callbackName = `on${name.charAt(0).toUpperCase() + name.slice(1).replace(/-./g, (x) => x[1].toUpperCase())}`;
    if (this[callbackName] && typeof this[callbackName] === "function") {
      const result = this[callbackName](detail);
      if (result === false) return false;
    }

    // Also support onNavigator[Name] pattern for backward compatibility
    const legacyName = `onNavigator${name.charAt(0).toUpperCase() + name.slice(1).replace(/-./g, (x) => x[1].toUpperCase())}`;
    if (this[legacyName] && typeof this[legacyName] === "function") {
      const result = this[legacyName](detail);
      if (result === false) return false;
    }

    // For addEventListener - dispatch standard CustomEvent
    const e = new CustomEvent(`navigator-${name}`, {
      detail,
      bubbles: true,
      composed: true,
      cancelable: true,
    });

    return this.dispatchEvent(e);
  }

  _shouldShow(node) {
    if (typeof this.shouldShow === "function") return this.shouldShow(node);
    return true;
  }

  _transformNode(node) {
    if (typeof this.transformNode === "function") {
      const result = this.transformNode(node);
      // If returns null/false/undefined, skip this node
      if (!result) return null;
      // Otherwise use the returned node (could be original or replacement)
      return result;
    }
    return node;
  }

  _getIcon(key) {
    return unsafeHTML(this.icons[key] || this.icons["default"]);
  }
  registerIcon(key, icon) {
    if (Array.isArray(key)) {
      for (const k of key) {
        if (k?.key && k?.icon) {
          this.registerIcon(k?.key, k?.icon);
        }
      }
      this.requestUpdate();
      return;
    }
    if (typeof key === "object") {
      if (key?.key && key?.icon) {
        this.icons[key?.key] = key?.icon;
      }
      this.requestUpdate();
      return;
    }
    if (!this.icons) this.icons = {};
    this.icons[key] = icon;
    this.requestUpdate();
  }
  _handleGlobalClick(e) {}

  _handleKeydown(e) {
    // Dispatch keyboard event for user to handle - pass only key data, not raw event
    if (!this._dispatch("keydown", { key: e.key, code: e.code, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, metaKey: e.metaKey, selection: this.selection })) {
      e.preventDefault();
    }
  }

  // --- ACTIONS ---

  _toggleVisibility(e, node) {
    e.stopPropagation();
    const isHidden = node.hidden || node.getAttribute("data-hidden") === "true";
    // Dispatch event with the desired new state - let user handle actual DOM changes
    this._dispatch("visibility-change", { node, visible: isHidden });
    this.requestUpdate();
  }

  _toggleLock(e, node) {
    e.stopPropagation();
    const isLocked = node.getAttribute("data-locked") === "true";
    // Dispatch event with the desired new state - let user handle actual attribute changes
    this._dispatch("lock-change", { node, locked: !isLocked });
    this.requestUpdate();
  }

  _deleteNode(e, node) {
    e.stopPropagation();
    // Dispatch event - let user handle actual node removal
    this._dispatch("delete", { node });
    this.requestUpdate();
  }

  // --- DRAG ---
  setTooltip = (show, x, y) => {
    const el = this.querySelector("#rdn-tooltip");
    if (!el) return;
    if (show) {
      el.classList.add("show");
      //el.style.transform = `translate(${x + 15}px, ${y + 15}px)`;
      el.style.top = `${y + 15}px`;
      el.style.left = `${x + 15}px`;
    } else {
      el.classList.remove("show");
    }
  };
  _initCanvasDrag() {
    const root = this.root;
    if (!root) return;

    root.addEventListener("dragstart", (e) => {
      if (e.target === root) {
        e.preventDefault();
        return;
      }
      if (!this._dispatch("drag-start", { node: e.target })) {
        e.preventDefault();
        return;
      }
      dragSourceNode = e.target;
      e.target.classList.add("dragging-source");
      e.dataTransfer.effectAllowed = "move";
    });

    root.addEventListener("dragend", (e) => {
      if (e.target) e.target.classList.remove("dragging-source");
      this._dispatch("drag-end", { node: e.target });
      dragSourceNode = null;
      this._dropTarget = null;
      this._dropPos = null;
      this.setTooltip(false);
      this.requestUpdate();
    });

    root.addEventListener("dragover", (e) => {
      e.preventDefault();
      if (!dragSourceNode) return;
      if (dragSourceNode.contains(e.target) || dragSourceNode === e.target) {
        e.dataTransfer.dropEffect = "move";
        this.setTooltip(true, e.clientX, e.clientY);
        return;
      }
      const rect = e.target.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const h = rect.height;
      let pos = "inside";
      if (y < h * 0.25) pos = "before";
      else if (y > h * 0.75) pos = "after";
      if (
        !this._dispatch("drag-over", {
          source: dragSourceNode,
          target: e.target,
          position: pos,
        })
      ) {
        this.setTooltip(true, e.clientX, e.clientY);
        return;
      }
      this.setTooltip(false);
      e.dataTransfer.dropEffect = "move";
    });

    root.addEventListener("drop", (e) => {
      e.preventDefault();
      this.setTooltip(false);
      if (!dragSourceNode) return;
      const rect = e.target.getBoundingClientRect();
      let pos = "inside";
      const y = e.clientY - rect.top;
      const h = rect.height;
      if (y < h * 0.25) pos = "before";
      else if (y > h * 0.75) pos = "after";
      this._executeMove(dragSourceNode, e.target, pos);
    });
  }

  _executeMove(source, target, position) {
    if (source.contains(target) || source === target) return;
    if (!this._dispatch("move", { source, target, position })) return;

    // Let user handle the actual DOM move via event listener
    // Navigator only updates its own visualization state
    if (position === "inside") {
      this.expand(target);
    }
    this._dropTarget = null;
    dragSourceNode = null;
    this.requestUpdate();
  }

  _onTreeDragStart(e, node) {
    e.stopPropagation();
    if (!this._dispatch("drag-start", { node })) {
      e.preventDefault();
      return;
    }
    dragSourceNode = node;
    e.dataTransfer.effectAllowed = "move";
  }

  _onTreeDragOver(e, node) {
    e.preventDefault();
    e.stopPropagation();
    if (!dragSourceNode || dragSourceNode === node || dragSourceNode.contains(node)) {
      this._dropTarget = null;
      // this._forbidden = true;
      this.setTooltip(true, e.clientX, e.clientY);
      console.log("forbidden");
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;
    let pos = "inside";
    if (y < h * 0.25) pos = "before";
    else if (y > h * 0.75) pos = "after";
    const tag = node.tagName.toLowerCase();
    if (pos === "inside" && ["img", "input", "hr"].includes(tag)) pos = "after";
    if (
      !this._dispatch("drag-over", {
        source: dragSourceNode,
        target: node,
        position: pos,
      })
    ) {
      this._dropTarget = null;
      //this._forbidden = true;
      this.setTooltip(true, e.clientX, e.clientY);
      return;
    }
    // this._forbidden = false;
    this.setTooltip(false);
    this._dropTarget = node;
    this._dropPos = pos;
    this.requestUpdate();
  }

  _onTreeDrop(e, node) {
    e.preventDefault();
    e.stopPropagation();
    // this._forbidden = false;
    this.setTooltip(false);
    if (!dragSourceNode || !this._dropTarget) return;
    // Dispatch drop event before move
    if (!this._dispatch("drop", { source: dragSourceNode, target: node, position: this._dropPos })) return;
    this._executeMove(dragSourceNode, node, this._dropPos);
  }

  _onTreeDragEnd(e, node) {
    e.stopPropagation();
    this._dispatch("drag-end", { node });
    dragSourceNode = null;
    this._dropTarget = null;
    this._dropPos = null;
    this.setTooltip(false);
    this.requestUpdate();
  }

  _renderTree(node, depth = 0) {
    if (node.nodeType !== Node.ELEMENT_NODE) return nothing;
    if (!this._shouldShow(node)) return nothing;

    // Apply transform filter - allows intercepting/replacing nodes
    const originalNode = node;
    const transformedNode = this._transformNode(node);
    if (!transformedNode) return nothing;

    // Prevent infinite recursion: if transform returned an ancestor of the original node,
    // the children would also transform to the same ancestor, creating a loop
    if (transformedNode !== originalNode && transformedNode.contains(originalNode)) {
      // Skip rendering children of originalNode since they'll transform to same parent
      // Instead, we'll let the parent render naturally from the tree traversal
      return nothing;
    }

    // Use transformed node for rendering
    node = transformedNode;

    const isSelected = this.selection === node;
    const isExpanded = this._expanded.has(node);

    // Check if any children would actually be rendered (after filtering and transforming)
    let hasChildren = false;
    if (node.children.length > 0) {
      for (const child of node.children) {
        if (child.nodeType !== Node.ELEMENT_NODE) continue;
        if (!this._shouldShow(child)) continue;
        const transformedChild = this._transformNode(child);
        if (!transformedChild) continue;
        // If child transforms to this same node (parent), skip it
        if (transformedChild === node) continue;
        // If child transforms to an ancestor of itself, skip it
        if (transformedChild !== child && transformedChild.contains(child)) continue;
        // At least one child will render
        hasChildren = true;
        break;
      }
    }

    const tagName = node.tagName.toLowerCase();
    const displayName = node.getAttribute("data-name") || node.getAttribute("data-tagname") || tagName;
    const id = node.id ? "#" + node.id : "";

    let iconKey = "default";
    const type = node.getAttribute("data-type");
    if (type && this.icons[type]) iconKey = type;
    else if (["div", "section", "header", "footer"].includes(tagName)) iconKey = "container";
    else if (["h1", "h2", "h3", "p", "span"].includes(tagName)) iconKey = "text";
    else if (tagName === "img") iconKey = "image";
    else if (tagName === "button") iconKey = "button";
    else if (tagName === "a") iconKey = "anchor";
    const paddingLeft = `${depth * 12 + 6}px`;

    const isHidden = node.hidden || node.getAttribute("data-hidden") === "true";
    const isLocked = node.getAttribute("data-locked") === "true";

    let nodeAllowedActions = this.actions || [];
    const localAttr = node.getAttribute("data-navigator-actions");
    if (localAttr !== null) nodeAllowedActions = localAttr ? localAttr.split(",").map((s) => s.trim()) : [];

    return html`
      <div
        class="rdn-node ${isSelected ? "selected" : ""} ${this._dropTarget === node && this._dropPos === "inside" ? "drag-over-inside" : ""}"
        style="padding-left: ${paddingLeft}"
        draggable="true"
        .node=${node}
        @click=${(e) => {
          if (!this._dispatch("click", { node })) return;
          this.select(node);
        }}
        @dblclick=${(e) => {
          e.stopPropagation();
          if (!this._dispatch("dblclick", { node })) return;
          this.toggle(node);
        }}
        @contextmenu=${(e) => {
          if (!this._dispatch("contextmenu", { node })) {
            e.preventDefault();
          }
        }}
        @dragstart=${(e) => this._onTreeDragStart(e, node)}
        @dragover=${(e) => this._onTreeDragOver(e, node, true)}
        @drop=${(e) => this._onTreeDrop(e, node)}
        @dragend=${(e) => this._onTreeDragEnd(e, node)}
        @mouseenter=${() => this._dispatch("hover-start", { node })}
        @mouseleave=${() => this._dispatch("hover-end", { node })}
      >
        ${this._dropTarget === node && this._dropPos !== "inside" ? html` <div id="rdn-drop-line" style="display:block; top: ${this._dropPos === "before" ? "0" : "100%"}; left: ${paddingLeft}"></div> ` : nothing}

        <div
          class="rdn-toggle ${hasChildren ? "" : "invisible"}"
          @click=${(e) => {
            e.stopPropagation();
            this.toggle(node);
          }}
        >
          ${isExpanded ? this._getIcon("chevronDown") : this._getIcon("chevronRight")}
        </div>

        <div class="rdn-icon">${this._getIcon(iconKey)}</div>
        <span class="rdn-label">${displayName}</span>
        ${id ? html`<span class="rdn-meta">${id}</span>` : ""}

        <div class="rdn-node-actions ${isHidden || isLocked ? "visible" : ""}">
          ${this.nodeActions.map((action) => {
            if (!nodeAllowedActions.includes(action.id)) return nothing;
            const isActive = action.isActive ? action.isActive(node) : false;
            const iconName = action.getIcon ? action.getIcon(node) : "default";

            return html`
              <div
                class="rdn-action-btn ${action.className || ""} ${isActive ? "active" : ""}"
                title="${action.title}"
                @click=${(e) => {
                  if (!this._dispatch("action-click", { action: action.id, node })) return;
                  action.onClick(e, node, this);
                }}
              >
                ${this._getIcon(iconName)}
              </div>
            `;
          })}
        </div>
      </div>
      ${isExpanded ? Array.from(node.children).map((c) => this._renderTree(c, depth + 1)) : nothing}
    `;
  }
  addShield = () => {
    const canvas = document.getElementById("canvas");
    if (canvas && canvas.classList && !canvas.classList.contains("disabled")) canvas.classList.add("disabled");
  };
  removeShield = () => {
    const canvas = document.getElementById("canvas");
    if (canvas) canvas.classList.remove("disabled");
  };
  _dragHeader(e) {
    // Prevent drag if clicking on buttons inside header
    if (e.target.closest(".rdn-header-actions")) return;

    this._dragState.isDragging = true;
    this._dragState.startX = e.clientX;
    this._dragState.startY = e.clientY;

    // Get the initial top/left to lock absolute positioning
    const rect = this.getBoundingClientRect();
    this._dragState.initialLeft = rect.left;
    this._dragState.initialTop = rect.top;

    // Switch to fixed left/top logic to break any "right" anchoring
    this.style.left = `${rect.left}px`;
    this.style.top = `${rect.top}px`;
    this.style.right = "auto";
    this.style.bottom = "auto";
    this.addShield();

    // Activate shield to catch events over iframes/canvas
    document.body.classList.add("is-dragging-nav");

    const onMove = (em) => {
      const dx = em.clientX - this._dragState.startX;
      const dy = em.clientY - this._dragState.startY;
      let top = Math.max(0, this._dragState.initialTop + dy);
      let left = this._dragState.initialLeft + dx;
      this.style.top = `${top}px`;
      this.style.left = `${left}px`;
    };
    const onUp = () => {
      this._dragState.isDragging = false;
      document.body.classList.remove("is-dragging-nav");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      this.removeShield();
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  _dragResize(e) {
    e.stopPropagation();
    e.preventDefault();
    this._dragState.isResizing = true;

    const rect = this.getBoundingClientRect();
    this.style.left = `${rect.left}px`;
    this.style.top = `${rect.top}px`;
    this.style.right = "auto";
    this.style.bottom = "auto";

    const startX = e.clientX;
    const startY = e.clientY;
    const startW = this.offsetWidth;
    const startH = this.offsetHeight;
    this.addShield();
    document.body.classList.add("is-dragging-nav");

    const onMove = (em) => {
      this.addShield();
      this.style.width = `${Math.max(200, startW + (em.clientX - startX))}px`;
      this.style.height = `${Math.max(200, startH + (em.clientY - startY))}px`;
    };
    const onUp = () => {
      document.body.classList.remove("is-dragging-nav");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      this.removeShield();
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  render() {
    // Ensure Root is Connected
    if (!this.root || !this.root.isConnected) {
      return html`
        <style>
          ${this.constructor.styles.cssText}
        </style>
        <div class="rdn-window-header" @mousedown=${this._dragHeader.bind(this)}>
          <div class="window-title">Navigator</div>
        </div>
        <div style="padding:20px; text-align:center; color:var(--text-muted);">No Canvas Root</div>
        <div class="rdn-resizer" @mousedown=${this._dragResize.bind(this)}></div>
      `;
    }

    return html`
      <style>
        ${this.constructor.styles.cssText}
      </style>
      <div class="rdn-window-header" @mousedown=${this._dragHeader.bind(this)}>
        <div class="window-title">Navigator</div>
        <div class="rdn-header-actions">
          ${this.headerActions.map(
            (act) =>
              html`<div
                class="rdn-h-btn"
                title="${act.title}"
                @click=${(e) => {
                  if (!this._dispatch("header-action-click", { action: act.id })) return;
                  act.onClick(this);
                }}
              >
                ${this._getIcon(act.icon)}
              </div>`,
          )}
        </div>
      </div>
      <div class="rdn-tree-container">${this._renderTree(this.root)}</div>
      <div id="rdn-tooltip" class="${this._forbidden ? "show" : ""}">🚫 Cannot Drop Here</div>
      <div class="rdn-resizer" @mousedown=${this._dragResize.bind(this)}></div>
    `;
  }
}

customElements.define("rd-navigator", DomNavigator);
